import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
  Divider,
  Stack,
  Paper,
  Button,
  Alert
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SpeedIcon from '@mui/icons-material/Speed';
import CodeIcon from '@mui/icons-material/Code';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SchoolIcon from '@mui/icons-material/School';
import FunctionsIcon from '@mui/icons-material/Functions';
import ScienceIcon from '@mui/icons-material/Science';
import BiotechIcon from '@mui/icons-material/Biotech';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import LanguageIcon from '@mui/icons-material/Language';
import StarIcon from '@mui/icons-material/Star';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import TuneIcon from '@mui/icons-material/Tune';
import ApiIcon from '@mui/icons-material/Api';
import DataObjectIcon from '@mui/icons-material/DataObject';
// Additional icons for more descriptive benchmarks
import TerminalIcon from '@mui/icons-material/Terminal';
import BugReportIcon from '@mui/icons-material/BugReport';
import WebIcon from '@mui/icons-material/Web';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import DiamondIcon from '@mui/icons-material/Diamond';
import CalculateIcon from '@mui/icons-material/Calculate';
import BuildIcon from '@mui/icons-material/Build';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import PaletteIcon from '@mui/icons-material/Palette';
import { llmModels } from '../pages/modelData';

// Helper function to get the appropriate icon for a metric
const getMetricIcon = (metricName) => {
  const lowerMetricName = metricName.toLowerCase();
  
  // SWE-bench and Terminal-bench - software engineering benchmarks
  if (lowerMetricName.includes('swe-bench') || lowerMetricName.includes('swe bench')) {
    return <BugReportIcon color="primary" />;
  } else if (lowerMetricName.includes('terminal-bench') || lowerMetricName.includes('terminal bench')) {
    return <TerminalIcon sx={{ color: 'info.main' }} />;
  }
  // Code-related benchmarks
  else if (lowerMetricName.includes('code') || lowerMetricName.includes('humaneval')) {
    return <CodeIcon color="primary" />;
  }
  // GPQA Diamond - scientific reasoning
  else if (lowerMetricName.includes('gpqa diamond')) {
    return <DiamondIcon sx={{ color: 'secondary.main' }} />;
  } else if (lowerMetricName.includes('gpqa')) {
    return <ScienceIcon color="secondary" />;
  }
  // AIME - mathematical reasoning
  else if (lowerMetricName.includes('aime')) {
    return <CalculateIcon sx={{ color: 'warning.main' }} />;
  }
  // Math-related benchmarks
  else if (lowerMetricName.includes('math') || lowerMetricName.includes('gsm8k')) {
    return <FunctionsIcon sx={{ color: 'info.main' }} />;
  }
  // Frontend and UI/UX benchmarks
  else if (lowerMetricName.includes('frontend dev') || lowerMetricName.includes('frontend')) {
    return <DeveloperModeIcon sx={{ color: 'primary.main' }} />;
  } else if (lowerMetricName.includes('ui/ux') || lowerMetricName.includes('ui') || lowerMetricName.includes('ux')) {
    return <PaletteIcon sx={{ color: 'secondary.main' }} />;
  }
  // Web-related benchmarks
  else if (lowerMetricName.includes('web')) {
    return <WebIcon sx={{ color: 'info.main' }} />;
  }
  // Speed benchmarks
  else if (lowerMetricName.includes('speed') || lowerMetricName.includes('t/s')) {
    return <SpeedIcon sx={{ color: 'success.main' }} />;
  }
  // Educational benchmarks
  else if (lowerMetricName.includes('mmlu')) {
    return <SchoolIcon sx={{ color: 'warning.main' }} />;
  }
  // Tool use and general capabilities
  else if (lowerMetricName.includes('tool use')) {
    return <BuildIcon sx={{ color: 'info.main' }} />;
  }
  // RAG and retrieval benchmarks
  else if (lowerMetricName.includes('rag')) {
    return <ApiIcon sx={{ color: 'info.main' }} />;
  }
  // SQL and data analysis benchmarks
  else if (lowerMetricName.includes('sql') || lowerMetricName.includes('data analysis')) {
    return <DataObjectIcon sx={{ color: 'info.main' }} />;
  }
  // Reasoning benchmarks
  else if (lowerMetricName.includes('reasoning')) {
    return <PsychologyIcon color="secondary" />;
  }
  // Language benchmarks
  else if (lowerMetricName.includes('multilingual') || lowerMetricName.includes('language') || 
            lowerMetricName.includes('cross-lingual') || lowerMetricName.includes('low-resource')) {
    return <LanguageIcon sx={{ color: 'info.dark' }} />;
  }
  // Default fallback
  return <ScienceIcon color="default" />;
};

const ModelDetail = ({ modelId: propModelId }) => {
  const { modelId: routeModelId } = useParams();
  const modelId = propModelId || routeModelId;
  const model = llmModels.find(m => m.id === modelId);

  if (!model) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4">Model not found</Typography>
      </Container>
    );
  }

  const metaDescription = `${model.name} by ${model.provider}: ${model.description} Features include ${model.features?.join(', ')}. View detailed specifications, benchmarks, and pricing.`;

  // Calculate apiProviderDisplay
  let apiProviderDisplay;
  if (model.isOpenSource) {
    apiProviderDisplay = "Primarily via self-hosting; other API providers may be listed if available";
    if (model.apiProviders && model.apiProviders.length > 0) {
      apiProviderDisplay = `Primarily via self-hosting. Also available via: ${model.apiProviders.join(', ')}`;
    } else {
      apiProviderDisplay = "Primarily via self-hosting; other API providers may be listed if available (none specified here).";
    }
  } else {
    if (model.apiProviders && model.apiProviders.length > 0) {
      apiProviderDisplay = model.apiProviders.join(', ');
    } else if (model.provider) {
      // For closed-source models without specific apiProviders, infer from provider.
      apiProviderDisplay = `${model.provider} API (details typically on provider website)`;
    } else {
      apiProviderDisplay = "Not specified";
    }
  }

  // Create JSON-LD schema data for the model
  const jsonLdData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": model.name,
    "description": model.description,
    "brand": {
      "@type": "Brand",
      "name": model.provider
    },
    "url": window.location.href
  };

  // Add pricing if available
  if (model.inputPrice || model.outputPrice) {
    jsonLdData.offers = {
      "@type": "Offer",
      "priceCurrency": "USD"
    };
    
    // Create offer description with both input and output prices if available
    let priceDescription = [];
    if (model.inputPrice) priceDescription.push(`Input: ${model.inputPrice}`);
    if (model.outputPrice) priceDescription.push(`Output: ${model.outputPrice}`);
    
    jsonLdData.offers.description = priceDescription.join(', ');
    
    // Use input price as the main price if available
    if (model.inputPrice) {
      // Try to extract just the number from formats like "3.00" or "$3.00" or "3.00 per 1M tokens"
      const priceMatch = model.inputPrice.toString().match(/(\d+(\.\d+)?)/);
      if (priceMatch && priceMatch[1]) {
        jsonLdData.offers.price = priceMatch[1];
      } else {
        jsonLdData.offers.price = model.inputPrice.toString();
      }
    }
  }

  return (
    <>
      <Helmet>
        <title>{`${model.name} - AI Model Details - AI Models Directory`}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={`${model.name} - AI Model Details`} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={`${model.name} - AI Model Details`} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href={`/models/${modelId}`} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdData)}
        </script>
      </Helmet>

      <Container maxWidth="lg" component="main">
        <Box sx={{ my: 4 }} component="article">
          <nav>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => window.history.back()}
              sx={{ mb: 3 }}
              aria-label="Return to directory"
            >
              Back to Directory
            </Button>
          </nav>

          <Paper elevation={3} sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <Grid container spacing={3} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} md={model.providerURL ? 8 : 12}>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    fontWeight: 'bold',
                    mb: 0.5,
                    background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block', 
                  }}
                >
                  {model.name}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ mb: 1, mt: 0.5 }}
                  component="p"
                >
                  By {model.provider}
                </Typography>
                {model.isOpenSource && (
                  <Chip
                    icon={<LockOpenIcon />}
                    label="Open Source"
                    variant="filled"
                    color="success"
                    sx={{
                      mb: 1,
                      backgroundColor: (theme) => alpha(theme.palette.success.main, 0.15),
                      color: 'success.dark',
                      '& .MuiChip-icon': { color: 'success.dark' },
                      fontWeight: 'medium',
                      border: (theme) => `1px solid ${theme.palette.success.dark}`,
                      '&:hover': {
                        boxShadow: (theme) => `0 0 10px ${alpha(theme.palette.success.main, 0.6)}`,
                        filter: 'brightness(1.05)'
                      }
                    }}
                  />
                )}
              </Grid>
              {model.providerURL && (
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Button
                    variant="contained"
                    color="primary"
                    href={model.providerURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<LaunchIcon />}
                    sx={{
                      '&:hover': {
                        transform: 'scale(1.03)',
                        boxShadow: (theme) => `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`
                      },
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                    }}
                  >
                    Visit Provider
                  </Button>
                </Grid>
              )}
            </Grid>
            <Typography variant="body1" paragraph component="p" sx={{ fontSize: '1.1rem' }}>
              {model.description}
            </Typography>
          </Paper>

          <Grid container spacing={4} component="section" sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }} component="section" aria-labelledby="tech-specs-title">
                <Typography variant="h3" sx={{ fontSize: 'h6.fontSize' }} gutterBottom id="tech-specs-title">
                  Technical Specifications
                </Typography>
                <List aria-label="Technical specifications list">
                  <ListItem>
                    <ListItemText
                      primary="Input Context Window"
                      secondary={model.specs?.find(spec => spec.name === "Input Context")?.value || "Not specified"}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="Maximum Output"
                      secondary={model.specs?.find(spec => spec.name === "Max Output")?.value || "Not specified"}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="Open Source"
                      secondary={model.isOpenSource ? "Yes" : "No"}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="Release Date"
                      secondary={model.specs?.find(spec => spec.name === "Release Date")?.value || "Not specified"}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="Knowledge Cut-off"
                      secondary={model.specs?.find(spec => spec.name === "Knowledge Cut-off")?.value || "Not specified"}
                    />
                  </ListItem>
                  {model.specs?.find(spec => spec.name === "Parameters") && (
                    <>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText
                          primary="Parameters"
                          secondary={model.specs.find(spec => spec.name === "Parameters").value}
                        />
                      </ListItem>
                    </>
                  )}
                  {model.specs?.find(spec => spec.name === "Model Type") && (
                    <>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText
                          primary="Model Type"
                          secondary={model.specs.find(spec => spec.name === "Model Type").value}
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }} component="section" aria-labelledby="features-title">
                <Typography variant="h3" sx={{ fontSize: 'h6.fontSize' }} gutterBottom id="features-title">
                  Availability & Features
                </Typography>
                <List aria-label="Availability and features list">
                  <ListItem>
                    <ListItemText
                      primary="API Providers"
                      secondary={apiProviderDisplay}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="Modalities"
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {(model.modalities || ['Text', 'Code', 'Vision']).map((modality) => (
                            <Chip
                              key={modality}
                              label={modality}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          ))}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="Key Features"
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {(model.features || []).map((feature) => (
                            <Chip
                              key={feature}
                              label={feature}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          ))}
                        </Box>
                      }
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {model.keyMetrics && model.keyMetrics.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }} component="section" aria-labelledby="benchmarks-title">
                <Typography variant="h3" sx={{ fontSize: 'h6.fontSize' }} gutterBottom id="benchmarks-title">
                  Benchmark Performance
                </Typography>
                <TableContainer>
                  <Table aria-label="Benchmark performance table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Benchmark</TableCell>
                        <TableCell align="right">Score</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {model.keyMetrics?.map((metric, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {getMetricIcon(metric.name)}
                            {metric.name}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={metric.score}
                              color={metric.score.includes('1') ? "primary" : "default"}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            )}

            <Grid item xs={12}>
              <Paper sx={{ p: 3 }} component="section" aria-labelledby="pricing-title">
                <Typography variant="h3" sx={{ fontSize: 'h6.fontSize' }} gutterBottom id="pricing-title">
                  Pricing
                </Typography>
                <List aria-label="Pricing information">
                  {model.isOpenSource ? (
                    // Logic for Open Source Models
                    model.pricing?.note ? (
                      <ListItem>
                        <ListItemText
                          primary="Deployment & Costs"
                          secondary={model.pricing.note}
                        />
                      </ListItem>
                    ) : (
                      <ListItem>
                        <ListItemText
                          primary="Pricing"
                          secondary="Open-source model. Costs typically involve self-hosting (infrastructure, maintenance). Refer to provider documentation for details."
                        />
                      </ListItem>
                    )
                  ) : model.pricing?.note ? (
                    // Logic for Closed Source Models with a specific note
                    <ListItem>
                      <ListItemText
                        primary="Deployment"
                        secondary={model.pricing.note}
                      />
                    </ListItem>
                  ) : (
                    // Logic for Closed Source Models without a specific note (show token prices)
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Input Processing"
                          secondary={
                            <Typography variant="body2" color="text.secondary" component="div">
                              {model.inputPrice ? `$${model.inputPrice} / M tokens` : "$0.20 / M tokens"}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Per million tokens processed
                              </Typography>
                            </Typography>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText
                          primary="Output Generation"
                          secondary={
                            <Typography variant="body2" color="text.secondary" component="div">
                              {model.outputPrice ? `$${model.outputPrice} / M tokens` : "$1.00 / M tokens"}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Per million tokens generated
                              </Typography>
                            </Typography>
                          }
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default ModelDetail; 